import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ModalWindow } from "@components/ModalWindow";
import { ThemedText as Text } from "@components/ThemedText";
import { V3Button } from "@components/v3/primitives";
import Checkbox from "@components/DropDown/styled/Checkbox";
import { Signature } from "@components/onboarding/eligibility/eligible";
import { investorNameSelector } from "@state/investor/investor.selectors";
import {
  ModalContainer,
  StepContent,
  BenefitsList,
  DeclarationList,
  CheckboxContainer,
  SignatureContainer,
  ButtonContainer,
  ImportantInfoList,
  ImportantInfoContainer,
} from "./styled";
import { logException } from "@helpers/logException/logException";
import { clientInvestSdk } from "@utils/clientInvestSdk";
import { fetchIfIsaAccountAPI } from "@state/ifIsa/ifIsa.actions";
import { toast } from "react-toastify";
import { useIfIsaAnalytics } from "@hooks/useIfIsaAnalytics";
import { IfIsaFullSourceProps } from "@ifgengineering/client-analytics/src/types";

interface IfIsaSetupFlowModalProps {
  show: boolean;
  onClose: () => void;
  onComplete?: () => void;
  pageSource?: IfIsaFullSourceProps["source"];
}

type Step = "info" | "declaration" | "confirmation";

interface StepProps {
  fullName: string;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
}

const StepContainer: React.FC<{ children: any }> = ({ children }) => {
  return <StepContent>{children}</StepContent>;
};

const StepTitle: React.FC<{ children: any }> = ({ children }) => (
  <Text type="T24" color="SLATE900" fontFamily="archiaregular">
    {children}
  </Text>
);

const StepDescription: React.FC<{
  children: any;
  style?: React.CSSProperties;
}> = ({ children, style }) => (
  <Text type="P16" color="SLATE800" style={style}>
    {children}
  </Text>
);

const StepButtons: React.FC<{
  currentStep: Step;
  canProceed: boolean;
  onBack: () => void;
  onNext: () => void;
  onSubmit?: () => Promise<boolean>;
  onComplete: () => void;
  pageSource?: IfIsaFullSourceProps["source"];
}> = ({
  currentStep,
  canProceed,
  onBack,
  onNext,
  onSubmit,
  onComplete,
  pageSource,
}) => {
  const {
    triggerIfIsaProceedToDeclarationClicked,
    triggerIfIsaConfirmRegistrationClicked,
  } = useIfIsaAnalytics();

  const getButtonText = () => {
    switch (currentStep) {
      case "confirmation":
        return "Continue";
      case "info":
        return "Create IF-ISA Account";
      case "declaration":
        return "Confirm";
      default:
        return "Next";
    }
  };

  const getButtonAction = () => {
    if (currentStep === "confirmation") {
      return () => {
        triggerIfIsaConfirmRegistrationClicked({
          source: pageSource,
          deal_name: "",
        });

        onComplete();
      };
    }

    if (currentStep === "declaration") {
      return async () => {
        try {
          if (onSubmit) {
            const success = await onSubmit();
            if (success) {
              onNext();
            }
          } else {
            onNext();
          }
        } catch (error) {
          // Error handling is done in the onSubmit function (toast, etc.)
          // We don't advance the step on error
          logException({ tag: "ifIsaModalStepButtons", exception: error });
        }
      };
    }

    return () => {
      triggerIfIsaProceedToDeclarationClicked({
        source: pageSource,
        deal_name: "",
      });

      onNext();
    };
  };

  return (
    <ButtonContainer>
      {currentStep !== "confirmation" && (
        <V3Button
          variant="outline"
          size="lg"
          onClick={onBack}
          testID="ifisa-setup-back"
        >
          Back
        </V3Button>
      )}
      <V3Button
        size="lg"
        onClick={getButtonAction()}
        disabled={currentStep !== "confirmation" && !canProceed}
        testID="ifisa-setup-next"
      >
        {getButtonText()}
      </V3Button>
    </ButtonContainer>
  );
};

export const IfIsaSetupFlowModal: React.FC<IfIsaSetupFlowModalProps> = ({
  show,
  onClose,
  onComplete,
  pageSource,
}) => {
  const [currentStep, setCurrentStep] = useState<Step>("info");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isCreatingIfIsa, setIsCreatingIfIsa] = useState(false);

  const dispatch = useDispatch();
  const fullName = useSelector(investorNameSelector);

  // Reset function to start the modal fresh
  const resetModal = () => {
    setCurrentStep("info");
    setTermsAccepted(false);
  };

  // Reset modal when it's closed
  const handleClose = () => {
    resetModal();
    onClose();
  };

  // Reset modal when it opens to ensure it always starts fresh
  useEffect(() => {
    if (show) {
      resetModal();
    }
  }, [show]);

  const handleNext = () => {
    if (currentStep === "info") {
      setCurrentStep("declaration");
    } else if (currentStep === "declaration" && termsAccepted) {
      setCurrentStep("confirmation");
    }
  };

  const handleBack = () => {
    if (currentStep === "info") {
      onClose();
      return;
    }
    if (currentStep === "declaration") {
      setCurrentStep("info");
    } else if (currentStep === "confirmation") {
      setCurrentStep("declaration");
    }
  };

  const handleSubmitIfIsa = async () => {
    if (isCreatingIfIsa) return false; // Prevent multiple submissions

    setIsCreatingIfIsa(true);
    try {
      await clientInvestSdk.createIfIsaAccount({
        signedAt: new Date().toISOString(),
      });
      dispatch(fetchIfIsaAccountAPI());
      toast.success("IF-ISA account created successfully!", {
        toastId: "if-isa-created",
        autoClose: 3000,
      });
      return true;
    } catch (error) {
      logException({ tag: "createIfIsaAccount", exception: error });
      toast.error("Failed to create IF-ISA account. Please try again.", {
        toastId: "if-isa-error",
        autoClose: 5000,
      });
      return false;
    } finally {
      setIsCreatingIfIsa(false);
    }
  };

  const handleComplete = () => {
    onComplete?.();
    resetModal();
    onClose();
  };

  const canProceed = () => {
    if (currentStep === "info") return true;
    if (currentStep === "declaration") return termsAccepted;
    return true;
  };

  const renderStepContent = () => {
    const stepProps = {
      fullName,
      termsAccepted,
      onTermsChange: setTermsAccepted,
    };

    switch (currentStep) {
      case "info":
        return <InfoStep pageSource={pageSource} />;
      case "declaration":
        return <DeclarationStep {...stepProps} />;
      case "confirmation":
        return <ConfirmationStep pageSource={pageSource} />;
      default:
        return null;
    }
  };

  return (
    <ModalWindow show={show} onClose={handleClose} width="600px">
      <ModalContainer>
        {renderStepContent()}

        <StepButtons
          currentStep={currentStep}
          canProceed={canProceed()}
          onBack={handleBack}
          onNext={handleNext}
          onSubmit={handleSubmitIfIsa}
          onComplete={handleComplete}
          pageSource={pageSource}
        />
      </ModalContainer>
    </ModalWindow>
  );
};

const InfoStep: React.FC<{ pageSource?: IfIsaFullSourceProps["source"] }> = ({
  pageSource,
}) => {
  const { triggerIfIsaDescriptionPageViewed } = useIfIsaAnalytics();

  useEffect(() => {
    triggerIfIsaDescriptionPageViewed({
      source: pageSource,
      deal_name: "",
    });
  }, []);

  const benefits = [
    <>
      Enjoy <strong>tax-free income</strong> on returns.
    </>,
    <>
      Contribute up to <strong>£20,000 annually</strong> across all of your
      ISAs.
    </>,
    <>
      If you withdraw an amount from your IF-ISA wrapper and redeposit it within
      the same tax year, it won't affect your annual allowance.
    </>,
  ];

  const importantInfo = [
    "You must be a UK resident and taxpayer to open an ISA.",
    "You must be 18 years of age or older.",
    "You can open multiple IF-ISAs within a tax year (so if you have an unfunded IF-ISA with another ISA manager you won't need to transfer it over to us).",
    "You can fund a maximum of £20,000 across all of your ISAs within a tax year (6 April - 5 April). This does not limit your investments with us, it just means you can't invest over £20,000 in a year through your ISA.",
    "If you have multiple ISAs, you can fund all of them, as long as you collectively invest no more than £20,000 in a tax year.",
  ];

  return (
    <StepContainer>
      <StepTitle>IF-ISA Benefits</StepTitle>

      <StepDescription>
        Cur8 Capital offer an IF-ISA, or Innovative Finance ISA. An ISA stands
        for Individual Savings Account, which is a tax-efficient way to save and
        invest money in the UK.
      </StepDescription>

      <StepDescription>
        ISAs allow you to earn interest, dividends, and capital gains without
        paying UK income tax or capital gains tax.
      </StepDescription>

      <BenefitsList>
        {benefits.map((benefit, index) => (
          <li key={index}>
            <Text type="P16" color="SLATE800">
              {benefit}
            </Text>
          </li>
        ))}
      </BenefitsList>

      <ImportantInfoContainer>
        <Text type="S16" color="SLATE900">
          Important Information
        </Text>
        <ImportantInfoList>
          {importantInfo.map((info, index) => (
            <li key={index}>
              <Text type="P12" color="SLATE800">
                {info}
              </Text>
            </li>
          ))}
        </ImportantInfoList>
      </ImportantInfoContainer>
    </StepContainer>
  );
};

const DeclarationStep: React.FC<StepProps> = ({
  fullName,
  termsAccepted,
  onTermsChange,
}) => {
  const declarations = [
    "I authorise Cur8 Capital (trading name of IFG.VC limited) to hold my cash subscription, ISA investments, dividends and any other rights or proceeds in respect of those investments and any other cash; and to make on my behalf any claims to tax relief in respect of ISA investments.",
    "I declare that I am a UK tax resident.",
    "I declare that I am 18 years of age or older.",
    <>
      I, <strong>{fullName}</strong>, declare that this application form has
      been completed to the best of my knowledge and belief.
    </>,
  ];

  return (
    <StepContainer>
      <StepTitle>IF-ISA Declaration</StepTitle>

      <Text type="S16" color="SLATE900">
        I apply to subscribe for an Innovative Finance ISA for the current tax
        year and each successive year until further notice.
      </Text>

      <DeclarationList>
        {declarations.map((declaration, index) => (
          <li key={index}>
            <Text type="P16" color="SLATE800">
              {index + 1}.
            </Text>
            <Text type="P16" color="SLATE800">
              {declaration}
            </Text>
          </li>
        ))}
      </DeclarationList>

      <StepDescription style={{ fontStyle: "italic" }}>
        You have up to 14 days to cancel your IF-ISA if you change your mind.
      </StepDescription>

      <StepDescription>
        Please read and accept the Terms & Conditions. These are important and
        form your contract with us.
      </StepDescription>

      <CheckboxContainer>
        <Checkbox
          checked={termsAccepted}
          onChange={() => onTermsChange(!termsAccepted)}
          label="I accept the terms and conditions that apply to my account."
          labelFontSize="P16"
          labelColor="SLATE800"
        />
        {termsAccepted && (
          <SignatureContainer>
            <Signature name={fullName} date={new Date().toLocaleDateString()} />
          </SignatureContainer>
        )}
      </CheckboxContainer>
    </StepContainer>
  );
};

const ConfirmationStep: React.FC<{
  pageSource?: IfIsaFullSourceProps["source"];
}> = ({ pageSource }) => {
  const { triggerIfIsaRegistrationSuccessViewed } = useIfIsaAnalytics();

  useEffect(() => {
    triggerIfIsaRegistrationSuccessViewed({
      source: pageSource,
      deal_name: "",
    });
  }, []);

  return (
    <StepContainer>
      <StepTitle>IF-ISA Application Received</StepTitle>

      <StepDescription>
        We'll send you an email once your IF-ISA account is approved. In the
        meantime, you can continue to make an investment.
      </StepDescription>
    </StepContainer>
  );
};
